import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Input } from 'ui-components/components/input';
import { testId } from 'ui-components/services/testid-service';

const DEFAULT_TYPE = 'text';

interface CustomsTextInputProps {
  value?: string | number;
  onChange: (value: string | number) => void;
  type?: string;
  className?: string | null;
  disabled?: boolean;
  'data-testid'?: string | null;
  'aria-label'?: string;
  maxLength?: number;
}

export const CustomsTextInput: React.FC<CustomsTextInputProps> = ({
  value = '',
  onChange: externalOnChange,
  type = DEFAULT_TYPE,
  className = null,
  disabled = false,
  'data-testid': dataTestId = null,
  'aria-label': ariaLabel = '',
  maxLength,
}) => {
  const [stateValue, setStateValue] = useState(value ?? '');
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    // value can be null (not just undefined) for fields the API hasn't populated yet; the
    // `value = ''` default parameter above only covers undefined, so guard here too.
    setStateValue(value ?? '');
  }, [setStateValue, value]);

  const onBlur = useCallback(() => {
    isFocusedRef.current = false;
    const changedValue = type === 'number' ? parseInt(String(stateValue), 10) : stateValue;
    externalOnChange(changedValue);
  }, [externalOnChange, stateValue, type]);

  const onFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const onChange = useCallback(
    (updatedValue: string | number) => {
      setStateValue(updatedValue);
    },
    [setStateValue],
  );

  return (
    <Input
      {...testId(dataTestId)}
      aria-label={ariaLabel}
      className={className}
      disabled={disabled}
      maxLength={maxLength}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      type={type}
      value={stateValue}
    />
  );
};
